import { css } from 'lit';
/*
 * Shared icon-button treatment. Every clickable icon on the page uses `.icon-btn`
 * — no bespoke button chrome. Hover = slightly larger + blue (gives icons a bit
 * of texture), nothing else. Imported into each component's `static styles`
 * array so the rule lives in exactly one place. Mirrors the vanilla
 * `vg-icon-btn` mixin in symbolGrid.scss so Lit + grid icon buttons match.
 *
 * Optional size modifiers set the box (the glyph size is the <vitesse-icon size>
 * prop): `.icon-btn.lg` (header/menu, 18px glyph), `.icon-btn.md` (16px), and
 * `.icon-btn.sm` (13px) — same scale as the grid. A button with no size class
 * just wraps its glyph.
 *
 * Transitions transform + color only (never background) — consistent with the
 * unified theme-swap rule in core/theme.ts.
 */
export const iconButton = css `
  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    transition:
      transform 0.15s ease,
      color 0.15s ease;
  }
  .icon-btn:hover {
    transform: scale(1.07);
    color: var(--blue);
  }
  /* Danger / active-toggle hover = red (turn-off intent), matching the canonical
     _icon-button.scss spec. */
  .icon-btn.danger:hover {
    color: var(--red, #e03e42);
  }
  .icon-btn.active {
    color: var(--blue);
  }
  .icon-btn.active:hover {
    color: var(--red, #e03e42);
  }
  .icon-btn.sm {
    width: 22px;
    height: 22px;
  }
  .icon-btn.md {
    width: 28px;
    height: 28px;
  }
  .icon-btn.lg {
    width: 36px;
    height: 36px;
  }
`;
/*
 * The app's back affordance, for shadow-DOM components.
 *
 * A parallel definition of `.base-page-back-button` (base-page.scss) for the same reason
 * `iconButton` mirrors its SCSS mixin: a shadow root cannot @import the stylesheet, and the
 * alternative — every component inventing its own back link — is how "back" ends up looking
 * different on every surface.
 *
 * The label names the DESTINATION, not the act. "Back" tells you nothing you did not already know;
 * "Conversations" tells you where you are about to be.
 */
export const backButton = css `
  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    width: fit-content;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--blue-light, var(--blue));
    font: inherit;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    transition: opacity 0.2s ease;
  }
  .back-btn:hover {
    color: hsl(from var(--blue-light, var(--blue)) h s calc(l - 10%));
  }
  .back-btn:active {
    color: hsl(from var(--blue-light, var(--blue)) h s calc(l - 15%));
  }
`;
//# sourceMappingURL=shared.js.map